// ABOUTME: Sentry bootstrap: where errors go by domain (Sentry on .com, Metric on .ru), integrations, sampling and
// ABOUTME: the beforeSend filter that keeps browser noise (network, stale chunks, extensions, backend errors) out.

import * as Sentry from "@sentry/react"
import type { BrowserOptions, ErrorEvent, EventHint } from "@sentry/react"
import config from "../config"
import { runtimeTld } from "./domain"
import { appEnvironment } from "./environment"
import {
  isStorageRequest,
  scrubBreadcrumb,
  scrubRecordingEvent,
  scrubSpan,
  withoutStorageKeys,
} from "./sentryStorageLinks"
import { isChunkLoadError } from "./watchError.fn"

const messageOf = (error: unknown): string =>
  error instanceof Error
    ? error.message
    : typeof error === "string"
      ? error
      : typeof error === "object" && error !== null && "message" in error && typeof error.message === "string"
        ? error.message
        : ""

type NoiseRule = (message: string, error: unknown) => boolean

/**
 * What nobody can act on. Each rule is one kind of noise; the comment says whose
 * noise it is. A match drops the event.
 */
const NOISE_RULES: readonly NoiseRule[] = [
  // Network errors — the user's connection, not an app bug.
  // Chrome+Edge: "Failed to fetch"; Safari: "Load failed"; Firefox: "NetworkError when attempting to fetch resource".
  (msg) => msg === "Failed to fetch" || msg.startsWith("Failed to fetch ("),
  (msg) => msg === "Load failed" || msg.startsWith("Load failed (") || msg.includes("TypeError: Load failed"),
  (msg) => msg.includes("NetworkError when attempting to fetch"),
  // Aborted requests — navigation away, expected behavior
  (msg) => msg.includes("Fetch is aborted") || msg.includes("The operation was aborted") || msg.includes("AbortError"),
  (_msg, error) => error instanceof DOMException && error.name === "AbortError",
  // MIME type errors — iOS app cache/SW serving HTML instead of JS
  (msg) => msg.includes("is not a valid JavaScript MIME type"),
  // Stale chunk/CSS errors — user on old version after deploy.
  // Covers Chrome ("Failed to fetch dynamically imported module"),
  // Firefox ("error loading dynamically imported module"),
  // and the "Importing a module script failed" / "Unable to preload CSS" variants.
  (msg, error) => isChunkLoadError(error) || isChunkLoadError(msg),
  // Native bridge errors from iOS WebView — not frontend bugs
  (msg) => msg.includes("Method not found"),
  // iOS Safari backgrounded fetch / media decoding — flaky native, not our code
  (msg) => msg.includes("NotReadableError") && msg.includes("I/O read operation failed"),
  (msg) => msg.includes("EncodingError") && msg.includes("Decoding failed"),
  // IndexedDB flakies — the browser drops or rejects the connection when the
  // tab has been backgrounded too long, hits storage pressure, or a private
  // mode quota expires. Not actionable from our side, the user just needs
  // to reload.
  (msg) => msg.includes("Indexed Database server"),
  // Disk-full variants — Webkit reports `QuotaExceededError ... indexedDB.open`,
  // Blink wraps the underlying leveldb error as `FILE_ERROR_NO_SPACE`.
  (msg) => msg.includes("QuotaExceededError") && msg.includes("indexedDB"),
  (msg) => msg.includes("FILE_ERROR_NO_SPACE"),
  // DOM manipulation by browser extensions (autofill, password managers, Grammarly)
  (msg) => msg.includes("removeChild") && msg.includes("not a child of this node"),
  (msg) => msg.includes("insertBefore") && msg.includes("not a child of this node"),
  (msg) => msg.includes("The object can not be found here"),
  // Browser extension injected a non-ASCII byte (e.g. U+009E) into our root
  // HTML before parse — the parser blames our document, but the source is
  // anonymous and we can't fix it.
  (msg) => msg.includes("illegal character U+"),
  // Third-party payment widget (/2/zp.js — Tinkoff / similar) referencing its
  // own globals before they bind. Not our bundle.
  (msg) => msg.includes("zp_token is not defined"),
  // ECharts 6 tooltip race: TooltipHTMLContent.setContent dereferences a null
  // `el` when the chart is disposed mid-render. Symbolicated as
  // `evaluating 'a.innerHTML=e+o'` (minifier names are stable per ECharts
  // release; revisit if we bump the library).
  (msg) => msg.includes("'a.innerHTML=e+o'"),
  // Expected backend business errors surfaced to users via toasts — not frontend bugs.
  // Apollo Client 4 surfaces these as unhandled rejections; we already handle them
  // at the call sites via onError handlers, so don't alert on them in Sentry.
  (msg) => /^(CombinedGraphQLErrors: )?Err[A-Z]/.test(msg),
]

/**
 * A DOM Event passed to `reject` (e.g. `img.onerror = reject`). The default title
 * "Event 'Event' (type=error) captured as promise rejection" tells us nothing, but
 * the event's target usually has the asset URL we need. Put it on the message and
 * fingerprint by tag so the issue becomes actionable instead of a mystery.
 */
const describeAssetLoadError = (event: ErrorEvent, domEvent: Event): ErrorEvent => {
  const target = (domEvent.target ?? domEvent.currentTarget) as
    | (HTMLElement & { src?: string; href?: string; currentSrc?: string })
    | null
  const tag = target?.tagName.toLowerCase() ?? "unknown"
  // A video's or a file's signed link is a key while it works: its query does not go out (sentryStorageLinks).
  const url = withoutStorageKeys(target?.src ?? target?.currentSrc ?? target?.href ?? "")
  const exc = event.exception?.values?.[0]
  if (exc) {
    exc.type = "AssetLoadError"
    exc.value = `<${tag}> failed to load${url ? ` (${url})` : ""}`
  }
  event.fingerprint = ["asset-load-error", tag]
  return event
}

/**
 * Sentry's beforeSend: null drops the event, otherwise it is sent as returned.
 */
export const filterSentryEvent = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
  const error = hint.originalException
  // `Promise.reject(null)` / `Promise.reject(undefined)` — no stack, no value,
  // no way to act. Sentry's default grouping bundles every one of these into
  // a single noisy issue.
  if (error === null || error === undefined) return null
  if (typeof Event !== "undefined" && error instanceof Event) return describeAssetLoadError(event, error)
  const message = messageOf(error)
  return NOISE_RULES.some((isNoise) => isNoise(message, error)) ? null : event
}

type Destination = Pick<BrowserOptions, "dsn" | "tunnel" | "integrations" | "profileSessionSampleRate">

/**
 * Traces and replays, the same on either destination. No span is made for a request to the storage — an upload's
 * chunk carries the session's key in its address; a replay's record of a resource's load has its key cut off.
 */
const tracingAndReplay = () => [
  Sentry.browserTracingIntegration({ shouldCreateSpanForRequest: (url) => !isStorageRequest(url) }),
  Sentry.replayIntegration({ beforeAddRecordingEvent: scrubRecordingEvent }),
]

/**
 * Where the errors go, chosen by the host's domain like the analytics (model/domain). A .ru host — the Russian version
 * of the app — reports to Metric, our Sentry-compatible tracker in Russia (errors.trainingendurance.ru), straight from
 * the browser: Metric answers CORS itself (`Access-Control-Allow-Origin: *` on its envelope endpoint). Its project
 * takes errors, traces and replays, but Metric has no profiles. Without Metric's DSN a .ru host reports nowhere:
 * Sentry is no fallback for it.
 */
const destination = (): Destination | null => {
  if (runtimeTld() === "ru") {
    if (!config.METRIC_DSN) return null
    return { dsn: config.METRIC_DSN, integrations: tracingAndReplay() }
  }
  if (!config.SENTRY_DSN) return null
  return {
    dsn: config.SENTRY_DSN,
    // Ship events through our own origin — the ingress proxies this /sentry path
    // to Sentry ingest. Direct *.sentry.io traffic is blocked by ad-blockers and
    // some RU providers: exactly the users whose errors we most need to see.
    // (Legacy `/tunnel` is being retired in favour of the shared /sentry proxy.)
    tunnel: "/sentry/api/4511144739143760/envelope/",
    integrations: [...tracingAndReplay(), Sentry.browserProfilingIntegration()],
    profileSessionSampleRate: appEnvironment === "production" ? 0.1 : 1.0,
  }
}

export const initSentry = (): void => {
  const target = destination()
  if (target === null) return
  const environment = appEnvironment
  Sentry.init({
    ...target,
    sendDefaultPii: true,
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,
    // Replays: только при ошибках
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    // Attach sentry-trace/baggage only to our own API. Matching the resolved
    // API URL (substring match) follows wherever config points it: same-origin
    // /graphql on app./eu./ru./app.ru in prod, the /gqlapi proxy path in dev.
    // A hardcoded host list would silently go stale — the previous gqlapi.*
    // regex stopped matching anything when the API moved under the app host.
    tracePropagationTargets: config.API_URL_TRAINING ? [config.API_URL_TRAINING] : [],
    environment,
    release: config.BUILD_VERSION,
    beforeSend: filterSentryEvent,
    beforeBreadcrumb: scrubBreadcrumb,
    beforeSendSpan: scrubSpan,
  })
}
